<template>
  <TransitionRoot appear :show="showModal" as="template">
    <Dialog as="div" :open="showModal" @close="closeModal">
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="min-h-screen px-4 text-center flex">
          <DialogOverlay class="fixed inset-0 bg-black opacity-30" />

          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <div
              class="inline-block w-full max-w-screen-sm p-6 m-auto text-left align-middle transition-all transform bg-white shadow-xl rounded"
              style="max-width: 500px"
            >
              <template v-if="internalField.type === 'text'">
                <div class="flex flex-row pb-4">
                  <div class="flex-1">
                    <h3 class="text-lg leading-6 text-brand-black">Text</h3>
                  </div>
                  <div class="flex-4 cursor-pointer" @click="closeModal">
                    <img class="w-3" src="@/assets/icons/close.svg" ref="close" />
                  </div>
                </div>

                <div class="py-4">
                  <SingleLineInput
                    :value="internalField.value"
                    @input="internalField.value = $event.target.value"
                  ></SingleLineInput>
                </div>
              </template>
              <template v-if="field.type === 'date'">
                <div class="flex flex-row pb-4">
                  <div class="flex-1">
                    <h3 class="text-lg leading-6 text-brand-black">Date</h3>
                  </div>
                  <div class="flex-4 cursor-pointer" @click="closeModal">
                    <img class="w-3" src="@/assets/icons/close.svg" ref="close" />
                  </div>
                </div>
                <div class="flex flex-row py-4">
                  <div class="w-1/2 md:flex-1 mr-3">
                    <DropDown
                      :options="months"
                      placeholder="Month"
                      @select="updateMonth"
                      class="month"
                    ></DropDown>
                  </div>
                  <div class="w-1/4 md:flex-1 mr-3">
                    <DropDown
                      :options="days"
                      placeholder="Day"
                      @select="updateDay"
                      class="day"
                    ></DropDown>
                  </div>
                  <div class="w-1/4 md:flex-1">
                    <input
                      type="text"
                      class="w-full px-4 py-2 border-1 border-brand-mid-gray shadow-sm rounded placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green text-sm md:text-base"
                      placeholder="Year"
                      :class="{ 'bg-brand-light-gray': !!year }"
                      v-model="year"
                      v-maska="'####'"
                      @blur="updateDateValue"
                    />
                  </div>
                </div>
              </template>

              <template v-if="internalField.type === 'initial'">
                <div class="flex flex-row pb-4">
                  <div class="flex-1">
                    <h3 class="text-lg leading-6 text-brand-black">Initial</h3>
                  </div>
                  <div class="flex-4 cursor-pointer" @click="closeModal">
                    <img class="w-3" src="@/assets/icons/close.svg" ref="close" />
                  </div>
                </div>

                <div class="py-1 flex items-center">
                  <div class="flex items-center w-20">
                    <SingleLineInput
                      :value="internalField.value"
                      @input="internalField.value = $event.target.value"
                      :maxLength="6"
                    ></SingleLineInput>
                  </div>
                  <p class="typed-signature ml-5" v-if="internalField.value">
                    {{ internalField.value }}
                  </p>
                </div>
              </template>

              <template v-if="internalField.type === 'signature'">
                <div class="flex flex-row pb-4">
                  <div class="flex-1">
                    <h3 class="text-lg leading-6 text-brand-black">Signature</h3>
                  </div>
                  <div class="flex-4 cursor-pointer" @click="closeModal">
                    <img class="w-3" src="@/assets/icons/close.svg" ref="close" />
                  </div>
                </div>

                <div class="py-2 mx-auto" style="width: 400px">
                  <!-- width: 200 height: 50 -->

                  <template v-if="signatureType === 'draw'">
                    <div
                      class="w-full border border-brand-mid-gray h-24 cursor-pointer rounded mx-auto"
                    >
                      <Signature
                        ref="modalSignature"
                        :value="
                          internalField.value
                            ? JSON.parse(internalField.value)
                            : internalField.value
                        "
                        @update="updateValue"
                        :type="'new'"
                      ></Signature>
                    </div>
                    <div class="py-1 mb-2">
                      <p
                        class="cursor-pointer text-brand-green type-signature-link"
                        @click="toggleSignatureType('type')"
                      >
                        Prefer to type your signature?
                      </p>
                    </div>
                  </template>
                  <template v-else>
                    <p class="typed-signature px-1 pb-3" v-if="internalField.value">
                      {{ internalField.value }}
                    </p>
                    <SingleLineInput
                      :value="internalField.value"
                      @input="internalField.value = $event.target.value"
                      :maxLength="25"
                    ></SingleLineInput>
                    <p
                      class="cursor-pointer text-brand-green type-signature-link mb-2"
                      @click="toggleSignatureType('draw')"
                    >
                      Prefer to draw your signature?
                    </p>
                  </template>

                  <div class="flex items-center justify-end mt-2"></div>
                </div>

                <div class="py-1">
                  <p
                    class="cursor-pointer underline text-center"
                    style="color: #989898"
                    @click="clearSignature"
                  >
                    Clear signature
                  </p>
                </div>
              </template>

              <div class="py-4">
                <div class="flex items-center justify-center mx-auto py-1" v-if="errorMessage">
                  <p class="text-brand-red">{{ errorMessage }}</p>
                </div>
                <div class="m-auto w-1/2">
                  <PrimaryButton @click="closeModal">Done</PrimaryButton>
                </div>
              </div>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
<script>
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogOverlay
  //DialogTitle,
} from "@headlessui/vue"

import moment from "moment"
import _ from "lodash"
import { months, days } from "@/constants/dateOptions"

export default {
  components: {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogOverlay
    // SingleLineInput
  },
  emits: ["close", "update"],
  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    field: {
      type: Object
    }
  },
  computed: {
    isValid() {
      if (this.field.required && !this.field.value) {
        return false
      }
      return true
    },
    isTextSignature() {
      if (!this.field.value) return false
      return !(this.field.value[0] === "[" && this.field.value[1] === "{")
    },
    validDate() {
      return moment(this.formattedDate, "YYYY-MM-DD", true).isValid()
    },
    formattedDate() {
      return `${this.year}-${this.month}-${this.day}`
    }
  },
  data() {
    return {
      signatureType: "draw",
      signature: null,
      errorMessage: "",
      months,
      days,
      year: null,
      month: null,
      day: null,
      internalField: null
    }
  },
  created() {
    if (this.field) {
      this.internalField = _.cloneDeep(this.field)
    }

    this.signatureType = this.isMobile ? "draw" : "type"
  },
  mounted() {},
  methods: {
    updateDateValue() {
      if (this.validDate) {
        this.updateValue(this.formattedDate)
      }
    },
    updateMonth(val) {
      this.month = val.value
      this.updateDateValue()
    },
    updateDay(val) {
      this.day = val.value
      this.updateDateValue()
    },
    toggleSignatureType(type) {
      if (this.signatureType === "signature") {
        this.$refs.modalSignature.clear()
      }
      this.signatureType = type
      this.internalField.value = null
    },
    openModal() {
      this.modalOpen = true
    },
    closeModal() {
      if (this.internalField.type === "signature") {
        this.internalField.signatureType = this.signatureType

        if (this.internalField.signatureType === "draw") {
          this.internalField.signatureImgUrl =
            this.$refs.modalSignature.signature.toDataURL("image/png")
        }
      }

      if (this.internalField.type === "date" && this.formattedDate && !this.validDate) {
        this.errorMessage = "Please enter a valid date"
        return
      }

      this.$emit("close", this.internalField)
    },
    async updateValue(data) {
      if (this.internalField.type === "signature") {
        this.internalField.value = JSON.stringify(data)
      } else {
        this.internalField.value = data
      }
    },
    clearSignature() {
      if (this.signatureType === "draw") {
        this.$refs.modalSignature.clear()
      }
      this.internalField.value = null
    },
    validateField() {
      if (!this.isValid) {
        if (this.field.required && !this.field.value) {
          this.errorMessage = "This field is required."
        }
      } else {
        this.errorMessage = ""
      }
    }
  }
}
</script>
<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Yellowtail&display=swap");

.typed-signature {
  font-family: "Yellowtail", cursive;
  font-size: 30px;
}
</style>
